import { useEffect, useState, type ClipboardEvent } from "react";

import { createPortal } from "react-dom";

import { Modal, Select } from "@eriol-io/design-system";

import { Button } from "@/components/ui/button";

import {
  buildSupportPreview,
  collectSupportBundle,
  submitSupportReport,
  type SupportBundle,
} from "@/services/supportReport";

interface ReportProblemModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * When set, the report is linked to this already-captured error (used by the
   * ErrorBoundary "Reportar este error" flow) so the crash rides alongside the
   * user's description in the diagnostics.
   */
  capturedError?: Error;
  /** Logical origin: about | menu-bar | error-boundary | mobile | web. */
  source?: string;
}

type Status = "idle" | "sending" | "success" | "error";

const CATEGORIES: { value: string; label: string }[] = [
  { value: "bug", label: "Algo no funciona" },
  { value: "question", label: "Tengo una duda" },
  { value: "billing", label: "Facturación / plan" },
  { value: "other", label: "Otro" },
];

/** Client-side ceiling for a pasted screenshot (backend caps at 25 MB/file). */
const MAX_SCREENSHOT_BYTES = 15 * 1024 * 1024;

/**
 * Raster-only allowlist for pasted screenshots. Excludes image/svg+xml (which
 * can carry active content) so we never attach a script-bearing "image".
 */
const ALLOWED_SCREENSHOT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
];

/**
 * "Reportar un problema" — native support report. Shared by Settings → About,
 * the "Ayuda" title-bar menu, and the ErrorBoundary fallback. Collects the
 * user's diagnostics + logs (eriol / gstreamer / cef / kero / renderer console) and
 * POSTs them to the backend (/support/reports). Transparent by design: a notice
 * explains what's attached and "Ver qué se envía" shows it before sending.
 */
export const ReportProblemModal = ({
  isOpen,
  onClose,
  capturedError,
  source = "about",
}: ReportProblemModalProps) => {
  const [text, setText] = useState("");
  const [category, setCategory] = useState("bug");
  const [status, setStatus] = useState<Status>("idle");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [collecting, setCollecting] = useState(false);
  const [bundle, setBundle] = useState<SupportBundle | null>(null);
  const [screenshot, setScreenshot] = useState<{
    file: File;
    url: string;
  } | null>(null);
  const [screenshotError, setScreenshotError] = useState<string | null>(null);

  // Reset transient state each time the modal is (re)opened. The screenshot is
  // cleared on every open/close flip — the component stays mounted while closed
  // (`if (!isOpen) return null`), so clearing it here is what releases its
  // object URL on close (via the [screenshot] cleanup effect below), not just
  // on the next reopen.
  useEffect(() => {
    if (isOpen) {
      setText("");
      setCategory("bug");
      setStatus("idle");
      setPreviewOpen(false);
      setBundle(null);
    }
    setScreenshot(null);
    setScreenshotError(null);
  }, [isOpen]);

  // Free the preview object URL whenever the screenshot is replaced/cleared or
  // the component unmounts (this cleanup runs with the previous value).
  useEffect(() => {
    return () => {
      if (screenshot) URL.revokeObjectURL(screenshot.url);
    };
  }, [screenshot]);

  /** Paste-to-attach: grab the first image off the clipboard as a screenshot. */
  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.type.startsWith("image/")) continue;
      if (!ALLOWED_SCREENSHOT_TYPES.includes(item.type)) {
        setScreenshotError(
          "Formato no soportado. Pegá una captura PNG, JPG, WebP o GIF.",
        );
        return;
      }
      const file = item.getAsFile();
      if (!file) continue;
      if (file.size > MAX_SCREENSHOT_BYTES) {
        setScreenshotError(
          "La imagen supera los 15 MB. Probá con una captura más liviana.",
        );
        return;
      }
      // Only hijack the paste once we're actually attaching a valid image, so a
      // rejected/mixed clipboard still lets normal text paste through.
      e.preventDefault();
      setScreenshotError(null);
      setScreenshot({ file, url: URL.createObjectURL(file) });
      return;
    }
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    setScreenshotError(null);
  };

  const ensureBundle = async (): Promise<SupportBundle> => {
    if (bundle) return bundle;
    const collected = await collectSupportBundle(capturedError);
    setBundle(collected);
    return collected;
  };

  const handleTogglePreview = async () => {
    if (previewOpen) {
      setPreviewOpen(false);
      return;
    }
    if (!bundle) {
      setCollecting(true);
      try {
        await ensureBundle();
      } finally {
        setCollecting(false);
      }
    }
    setPreviewOpen(true);
  };

  const handleSend = async () => {
    setStatus("sending");
    try {
      const message = text.trim() || "(sin descripción)";
      const collected = await ensureBundle();
      await submitSupportReport(
        {
          message,
          source,
          category,
          capturedError,
          screenshot: screenshot?.file,
        },
        collected,
      );
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  if (!isOpen) return null;

  const isSuccess = status === "success";
  const preview = bundle ? buildSupportPreview(bundle) : null;

  return createPortal(
    <Modal
      isOpen={isOpen}
      title="Reportar un problema"
      onClose={onClose}
      showCloseButton={true}
      footer={
        isSuccess ? (
          <div className="flex justify-end">
            <Button onClick={onClose}>Cerrar</Button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <Button
              variant="ghost"
              onClick={handleTogglePreview}
              disabled={collecting || status === "sending"}
            >
              {collecting
                ? "Preparando…"
                : previewOpen
                  ? "Ocultar"
                  : "Ver qué se envía"}
            </Button>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button onClick={handleSend} disabled={status === "sending"}>
                {status === "sending" ? "Enviando…" : "Enviar reporte"}
              </Button>
            </div>
          </div>
        )
      }
    >
      {isSuccess ? (
        <div className="flex flex-col gap-2 text-foreground">
          <p className="font-semibold text-base">¡Gracias por el reporte!</p>
          <p className="text-sm text-muted-foreground">
            Recibimos la información y los diagnósticos. La revisaremos desde
            soporte; no hace falta que hagas nada más.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3 text-foreground">
          <p className="text-sm text-muted-foreground">
            Contanos qué estabas haciendo y qué salió mal.
          </p>

          <Select
            value={category}
            onChange={setCategory}
            options={CATEGORIES}
            size="sm"
            variant="filled"
          />

          <div className="flex flex-col gap-1.5">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPaste={handlePaste}
              rows={5}
              maxLength={2000}
              autoFocus
              placeholder="Ej: puse un widget de QR y se rompió la vista previa…"
              className="w-full resize-none rounded-lg border border-border bg-secondary/30 p-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary/50 focus:outline-none"
            />
            <p className="text-xs text-muted-foreground/70">
              Podés pegar una captura de pantalla (Ctrl/⌘+V) para adjuntarla.
            </p>
          </div>

          {screenshot && (
            <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-secondary/20 p-2">
              <img
                src={screenshot.url}
                alt="Captura adjunta"
                className="h-16 w-16 shrink-0 rounded object-cover"
              />
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-xs text-foreground">
                  Captura adjunta
                </span>
                <span className="text-xs text-muted-foreground">
                  {Math.max(1, Math.round(screenshot.file.size / 1024))} KB
                </span>
              </div>
              <Button variant="ghost" onClick={removeScreenshot}>
                Quitar
              </Button>
            </div>
          )}

          {screenshotError && (
            <p className="text-xs text-destructive">{screenshotError}</p>
          )}

          <div className="rounded-lg border border-border/60 bg-secondary/20 p-3 text-xs text-muted-foreground">
            <p>
              Para ayudarte más rápido incluimos automáticamente diagnósticos:
              tu <strong>eriol.log</strong>, <strong>gstreamer.log</strong>,{" "}
              <strong>cef.log</strong>, <strong>kero.log</strong>, el registro
              del navegador y datos de versión/SO/GPU y tu sesión.
            </p>
          </div>

          {previewOpen && preview && (
            <div className="flex flex-col gap-2 rounded-lg border border-border/60 bg-background/40 p-3">
              <p className="text-xs font-semibold text-foreground">Adjuntos</p>
              <ul className="flex flex-col gap-1 text-xs text-muted-foreground">
                {preview.attachments.map((a) => (
                  <li key={a.name} className="flex justify-between gap-2">
                    <span>
                      {a.included ? "✓" : "—"} {a.name}
                    </span>
                    <span className="text-muted-foreground/70">
                      {a.sizeLabel}
                    </span>
                  </li>
                ))}
                {screenshot && (
                  <li className="flex justify-between gap-2">
                    <span>✓ captura de pantalla</span>
                    <span className="text-muted-foreground/70">
                      {Math.max(1, Math.round(screenshot.file.size / 1024))} KB
                    </span>
                  </li>
                )}
              </ul>
              <p className="mt-1 text-xs font-semibold text-foreground">
                Diagnósticos
              </p>
              <pre className="max-h-48 overflow-auto rounded bg-secondary/30 p-2 text-[11px] leading-snug text-muted-foreground">
                {JSON.stringify(preview.diagnostics, null, 2)}
              </pre>
            </div>
          )}

          {status === "error" && (
            <p className="text-xs text-destructive">
              No se pudo enviar ahora. Probá de nuevo en un momento.
            </p>
          )}
        </div>
      )}
    </Modal>,
    document.body,
  );
};

export default ReportProblemModal;
